import { TInvoiceSetting, TInvoiceTemplateItem, TUpdateInvoiceSettingReqDto } from "@/api/services/main/organization/invoice-setting/dtos/invoice-setting.dto";
import { TPersonInChargeUser } from "@/api/services/main/user/dtos/get-user-list.dto";
import {
  BILLING_MONTH_LABEL,
  BILLING_TREATMENT_LABEL,
  INVOICE_NUMBER_PREVIEW,
  TBillingMonth,
  TBillingTreatment,
} from "@/features/organization/invoice/constants/invoiceEnums";
import { TInvoiceDetail } from "@/features/organization/invoice/types/InvoiceDetail";
import { TInvoiceForm } from "@/features/organization/invoice/types/InvoiceForm";

/** Read-only view falls back to this when a value is unset (system default). */
const EMPTY_VALUE = "-";

/** 末日 / 「n日」 for the read-only view (day is null when 末日 is on). */
const formatBillingDay = (day: number | null | undefined, lastDay: boolean): string => {
  if (lastDay) return "末日";
  return day != null ? `${day}日` : EMPTY_VALUE;
};

const treatmentLabel = (treatment: TBillingTreatment | null): string =>
  treatment ? BILLING_TREATMENT_LABEL[treatment] : EMPTY_VALUE;

/** API setting -> react-hook-form values (numbers/ids become strings; nulls become ""). */
export const convertSettingToFormData = (setting: TInvoiceSetting): TInvoiceForm => ({
  billingMonth: setting.billingMonth,
  billingDay: setting.billingDay != null ? String(setting.billingDay) : "",
  billingDayLastDay: setting.billingDayLastDay,
  paymentCutoffMonth: setting.paymentCutoffMonth,
  paymentCutoffDay: setting.paymentCutoffDay != null ? String(setting.paymentCutoffDay) : "",
  paymentCutoffLastDay: setting.paymentCutoffLastDay,
  joinMonthTreatment: setting.joinMonthTreatment ?? "",
  leaveMonthTreatment: setting.leaveMonthTreatment ?? "",
  temporaryReturnTreatment: setting.temporaryReturnTreatment ?? "",
  leaveOfAbsenceTreatment: setting.leaveOfAbsenceTreatment ?? "",
  invoiceNumberFormat: setting.invoiceNumberFormat,
  templateId: setting.templateId != null ? String(setting.templateId) : "",
  defaultAssignee: setting.defaultAssigneeId != null ? String(setting.defaultAssigneeId) : "",
});

/** react-hook-form values -> PUT payload (days null when 末日; ids parsed to numbers). */
export const convertFormToUpdateReq = (data: TInvoiceForm): TUpdateInvoiceSettingReqDto => ({
  billingMonth: data.billingMonth as TBillingMonth,
  billingDay: data.billingDayLastDay ? null : Number(data.billingDay),
  billingDayLastDay: data.billingDayLastDay,
  paymentCutoffMonth: data.paymentCutoffMonth as TBillingMonth,
  paymentCutoffDay: data.paymentCutoffLastDay ? null : Number(data.paymentCutoffDay),
  paymentCutoffLastDay: data.paymentCutoffLastDay,
  joinMonthTreatment: data.joinMonthTreatment as TBillingTreatment,
  leaveMonthTreatment: data.leaveMonthTreatment as TBillingTreatment,
  temporaryReturnTreatment: data.temporaryReturnTreatment as TBillingTreatment,
  leaveOfAbsenceTreatment: data.leaveOfAbsenceTreatment as TBillingTreatment,
  invoiceNumberFormat: data.invoiceNumberFormat,
  templateId: data.templateId ? Number(data.templateId) : null,
  defaultAssigneeId: Number(data.defaultAssignee),
});

/** API setting (+ template / user masters) -> read-only 確認 display strings. */
export const convertSettingToDetail = (
  setting: TInvoiceSetting,
  templates: TInvoiceTemplateItem[],
  users: TPersonInChargeUser[],
): TInvoiceDetail => {
  const template = templates.find((item) => item.id === setting.templateId);
  const assignee = users.find((user) => user.id === setting.defaultAssigneeId);

  return {
    billingMonth: BILLING_MONTH_LABEL[setting.billingMonth] ?? EMPTY_VALUE,
    billingDay: formatBillingDay(setting.billingDay, setting.billingDayLastDay),
    paymentCutoffMonth: BILLING_MONTH_LABEL[setting.paymentCutoffMonth] ?? EMPTY_VALUE,
    paymentCutoffDay: formatBillingDay(setting.paymentCutoffDay, setting.paymentCutoffLastDay),
    joinMonth: treatmentLabel(setting.joinMonthTreatment),
    leaveMonth: treatmentLabel(setting.leaveMonthTreatment),
    temporaryReturn: treatmentLabel(setting.temporaryReturnTreatment),
    leaveOfAbsence: treatmentLabel(setting.leaveOfAbsenceTreatment),
    invoiceNumberFormat: setting.invoiceNumberFormat,
    invoiceNumberPreview: INVOICE_NUMBER_PREVIEW,
    templateName: template?.name ?? EMPTY_VALUE,
    defaultAssignee: assignee?.fullName ?? EMPTY_VALUE,
  };
};
